/* eslint-disable no-param-reassign */
import React, {
	useEffect, useRef, useState, memo, useMemo,
} from 'react';

import Hls from 'hls.js';

import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { getCurrentPageThumb } from '../../utils/media';
import { GreenroomState as GS } from '../../stores/types/types';
import type { MediaItem, PresentedMediaItem } from '../../stores/types/media';
import UseSyncMedia from '../main-video/use-sync-media';
import Socket from '../../connection/socket';
import { LOCATION_CANVAS_ROUTES } from '../../stores/reducers/video';
import { useAudioSink } from '../../hooks/devices/use-audio-sink';

type ProgressUpdate = {
	played: number;
	playedSeconds: number;
	loaded: number;
	loadedSeconds: number;
};
export type CrossOrigin = '' | 'anonymous' | 'use-credentials' | undefined;

type CustomMediaVideoProps = {
	className?: string;
	id?: string;
	// These values have defaults
	crossOrigin: CrossOrigin;
	playsInline?: boolean;
	onLoadedDataOverride?: () => void;
	onProgress?: (state: ProgressUpdate) => void;
	isEditingFeed?: boolean;
	mediaId: number;
	showbuilderMediaItem?:PresentedMediaItem;
	iterateLoadedMediaCount?: Function;
	socket?: Socket | null;
	doEchoCancel: boolean;
	audioOnly?: boolean;
	isForMainVideoEditingComponent?: boolean;
};

// Add custom props to all the optional props a video element could have
type MediaVideoProps = CustomMediaVideoProps & Partial<React.DOMAttributes<HTMLVideoElement>>;

const MediaVideoSpotlight = (
	{
		className,
		onLoadedDataOverride,
		onProgress,
		crossOrigin,
		isEditingFeed = false,
		mediaId,
		showbuilderMediaItem,
		iterateLoadedMediaCount,
		socket,
		doEchoCancel,
		audioOnly,
		isForMainVideoEditingComponent,
		...otherProps
	}: MediaVideoProps,
): JSX.Element => {
	const roomState = useSelector((state: GS) => state.MeetingReducer.roomState);
	const media = useSelector((state: GS) => state.MediaReducer.media);
	const presentedMedia = useSelector((state: GS) => state.MediaReducer.presentedMedia);
	const companionMode = useSelector((s: GS) => s.SessionReducer.companionMode);
	const onsiteLocationSession = useSelector((s: GS) => s.VideoReducer.onsiteLocationSession);
	const modalOpen = useSelector((s: GS) => s.ModalReducer.modalOpen);
	const mediaElRef = useRef<HTMLVideoElement>(null);
	const echoCancelAudioRef = useRef<HTMLAudioElement>(null);
	const mediaItem = useMemo(
		() => media.find((m: MediaItem) => m.id === mediaId),
		[mediaId, media],
	);

	const { applySinkId } = useAudioSink();

	const isInitialOnsiteLocationCanvas = useMemo(
		() => !!onsiteLocationSession
			&& LOCATION_CANVAS_ROUTES.includes(onsiteLocationSession.route)
			&& modalOpen,
		[onsiteLocationSession, modalOpen],
	);

	const presentedMediaItem = useMemo(
		() => (isEditingFeed ? showbuilderMediaItem : presentedMedia
			.find((m) => m.mediaId === mediaId)),
		[presentedMedia, mediaId, showbuilderMediaItem, isEditingFeed],
	);
	const hlsRef = useRef<Hls | null>(null);
	const timeUpdateRef = useRef<ProgressUpdate>({
		played: 0,
		playedSeconds: 0,
		loaded: 0,
		loadedSeconds: 0,
	});
	const [subsequentUnmount, setSubsequentUnmount] = useState(false);
	const [showPoster, setShowPoster] = useState(true);

	const src = useMemo(() => {
		const currentPage = presentedMediaItem?.currentPage ?? 1;
		const imageArray = mediaItem?.media_data.image_array ?? [''];
		return imageArray[currentPage - 1] ?? '';
	}, [
		mediaItem?.media_data.image_array,
		presentedMediaItem?.currentPage,
	]);

	const loop = !!mediaItem?.media_data.loop;

	const {
		onPlay,
		onEnded,
		onError,
		elapsed,
		duration,
		paused,
		mediaIsOnCanvas,
		onLoadedData: onLoadedDataInitial,
		...otherSyncMediaProps
	} = UseSyncMedia({
		mediaElRef,
		echoCancelAudioRef,
		mediaId,
		mediaItem,
		socket,
		iterateLoadedMediaCount,
		isEditingFeed,
		presentedMediaItem,
		isForMainVideoEditingComponent,
		src,
	});

	const onLoadedData = useMemo(() => {
		if (onLoadedDataOverride) {
			return onLoadedDataOverride;
		}
		return onLoadedDataInitial;
	}, [onLoadedDataInitial, onLoadedDataOverride]);

	const posterImage = useMemo(() => {
		if (roomState === ROOM_STATES.SHOWTIME) { return ''; }
		return isEditingFeed
			? getCurrentPageThumb(mediaItem?.media_data, presentedMediaItem?.currentPage)
			: src;
	}, [src, roomState, mediaItem, presentedMediaItem, isEditingFeed]);

	const isHlsVideo = useMemo((): boolean => src?.endsWith('.m3u8'), [src]);

	useEffect(() => {
		// Currently ivs creates an extra bit of duration for hls videos created
		// if the playSeconds is larger than the duration then we're at the end of the video.
		const oneFrame = 1 / 24;
		if (
			duration
				&& duration !== 0
				&& timeUpdateRef.current.playedSeconds > duration - oneFrame
				&& isHlsVideo
		) {
			if (loop) {
				(mediaElRef.current as any).seekTo?.(0);
			} else {
				onEnded?.();
			}
		}
	}, [
		duration,
		isHlsVideo,
		loop,
		onEnded,
		timeUpdateRef.current.playedSeconds,
	]);

	useEffect(() => {
		if (isHlsVideo && mediaElRef?.current && src) {
			// Destroy existing instance before creating new one
			if (hlsRef.current?.destroy) {
				try {
					hlsRef.current.destroy();
				} catch (err) {
					// Ignore SourceBuffer removed errors during cleanup
					if (!(err instanceof DOMException && err.message.includes('SourceBuffer'))) {
						console.error('Error destroying HLS instance:', err);
					}
				}
			}

			hlsRef.current = new Hls();
			hlsRef.current.loadSource(src);
			hlsRef.current.attachMedia(mediaElRef.current);

			return () => {
				if (isHlsVideo && hlsRef.current?.destroy) {
					try {
						hlsRef.current.destroy();
					} catch (err) {
						// Ignore SourceBuffer removed errors during cleanup
						if (!(err instanceof DOMException && err.message.includes('SourceBuffer'))) {
							console.error('Error destroying HLS instance:', err);
						}
					}
				}
			};
		}
		return undefined;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [src, isHlsVideo]);

	useEffect(() => () => {
		if (subsequentUnmount) {
			if (isHlsVideo && hlsRef.current?.destroy) {
				try {
					hlsRef.current.destroy();
				} catch (err) {
					// Ignore SourceBuffer removed errors during cleanup
					if (!(err instanceof DOMException && err.message.includes('SourceBuffer'))) {
						console.error('Error destroying HLS instance:', err);
					}
				}
			}
		}
		setSubsequentUnmount(true);
	}, [subsequentUnmount]);

	useEffect(() => {
		onProgress?.(timeUpdateRef.current);
	}, [onProgress]);

	useEffect(() => {
		if (showPoster && elapsed > 0) {
			setShowPoster(false);
		}
	}, [elapsed]);

	const consolidatedListeners = {
		onEnded,
		onError,
		onPlay,
		onProgress,
		...otherSyncMediaProps,
	};
	const moreProps = otherProps;

	// Apply setSinkId when echo cancellation is not used
	useEffect(() => {
		if (!doEchoCancel && mediaElRef.current) {
			applySinkId(mediaElRef.current);
		}
	}, [applySinkId, mediaElRef.current, doEchoCancel]);

	return (
			<>
			<div
				className={classNames('media-video-container', 'flex-center', 'spotlight-media-video', {
					'ivs-player-container': isHlsVideo,
				})}
			>
				<video
					className={classNames(className, { 'ivs-player-video': isHlsVideo })}
					src={!isHlsVideo ? src : undefined}
					crossOrigin={crossOrigin}
					playsInline={true}
					data-testid={`videoplayer-${false ? 'preloading' : 'loaded'}`}
					ref={mediaElRef}
					loop={loop}
					onLoadedData={onLoadedData}
					{ ...consolidatedListeners }
					{ ...moreProps }
					onPlaying={() => {
						if (showPoster) {
							setShowPoster(false);
						}
					}}
					onPause={(e) => {
						// the browser or dependencies may cause the video to pause when it shouldn't.
						const shouldNotPause = (
							!paused // video machine state is not paused
							&& !e.currentTarget.ended // video has not ended
							&& mediaIsOnCanvas // media is in current shot
							&& !presentedMediaItem?.videoState?.paused // sync state is not paused
							&& !isEditingFeed // not in show builder
						);
						if (shouldNotPause) {
							e.currentTarget.play();
						}
					}}
					muted={companionMode || isInitialOnsiteLocationCanvas}
				/>

				{posterImage && showPoster && (
					<div className='media-video-poster'>
						<img src={posterImage} alt={''} />
					</div>
				)}
			</div>
			{doEchoCancel
				? <audio
					id={`media-audio-${mediaId}`}
					ref={echoCancelAudioRef}
				></audio>
				: <></>
			}
			</>
	);
};

MediaVideoSpotlight.displayName = 'MediaVideoSpotlight';

export default memo(MediaVideoSpotlight);
